import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {StoresManager} from 'src/app/_global/stores-manager';
import {ProductsService} from 'src/app/_services/products.service';
import { ToastrService } from 'ngx-toastr';
import bwipjs from '@bwip-js/browser';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
import * as JsBarcode from 'jsbarcode';
const mmToPixels = 3.7795275591;

@Component({
    selector: 'print-labels',
    templateUrl: './print-labels.component.html',
})
export class PrintLabelsComponent implements OnInit {
    @Input() products: any[] = [];
    @Input() labelType: string;
    @Input() receivingFnsku: boolean = false;
    labels: any[] = [];
    allSizes = [{value: 30, text: '30 labels on Letter'},
        {value: 40, text: '40 labels on Letter'}, {value: 44, text: '44 labels on Letter'},
        {value: 1, text: 'Custom'}];
    formGroup: UntypedFormGroup;
    amazonStores: any;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        public stores: StoresManager,
        private productsService: ProductsService,
        private toastr: ToastrService,
    ) {
    }

    private normalizeDigits(text: string): string {
        return (text || '').replace(/\D/g, '');
    }

    // GS1 Mod10 check digit validation for GTIN-14
    private isValidGtin14(gtin14Digits: string): boolean {
        if (!/^\d{14}$/.test(gtin14Digits || '')) return false;

        let sum = 0;
        // Process first 13 digits from right to left (excluding check digit at index 13)
        for (let posFromRight = 0; posFromRight < 13; posFromRight++) {
            const digit = +gtin14Digits[12 - posFromRight];
            sum += digit * (posFromRight % 2 === 0 ? 3 : 1);
        }
        const expectedCheck = (10 - (sum % 10)) % 10;
        return expectedCheck === +gtin14Digits[13];
    }

    ngOnInit() {
        let printSettings = JSON.parse(localStorage.getItem('printSettings') || '{ "count": 30 }');
        this.formGroup = this.fb.group({
            count: [printSettings.count],
            width: [printSettings.width || 100, [Validators.required, Validators.min(25)]],
            height: [printSettings.height || 40, [Validators.required, Validators.min(25)]]
        });
        if (this.receivingFnsku == false) {
            this.labels = this.products.map(p => {
                let description = p.description || p.title || '';
                if (this.labelType == 'upc') {
                    return {sku: p.sku, description: description, label: p.upc, quantity: p.quantityShipped || p.quantity || 1};
                } else {
                    return {
                        sku: p.sku, storeName: p.storeName, storeType: p.storeType,
                        expiryDate: p.expiryDate ? p.expiryDate.substr(0, 10) : p.expiryDate,
                        description: description, label: p[this.labelType], quantity: p.quantityShipped || p.quantity || 1
                    };
                }
            });
        }
    }

    barcode(text: string) {
        let options = {width: 1, height: 25, fontSize: 10};
        if (this.labelType == 'gtin') {
            const digitsOnly = this.normalizeDigits(text);
            // Match reference: encode only the 14-digit GTIN (no AI 01 prefix)
            try {
                return bwipjs.toSVG({
                    bcid: 'code128',
                    text: digitsOnly,
                    includetext: true,
                    textxalign: 'center',
                    // quiet zones (points) + reasonable defaults for label printing
                    paddingwidth: 4,
                    paddingheight: 4,
                    scale: this.formGroup?.value?.count == 1 ? 3 : 2,
                    height: 10, // mm bar height
                } as any);
            } catch (e) {
                return '';
            }
        }

        if (this.labelType == 'upc') {
            const digitsOnly = this.normalizeDigits(text);
            const len = digitsOnly.length;
            options['format'] = len === 13 ? 'ean13' : 'upc';
            if (this.formGroup.value.count == 1) {
                options.width = ((this.formGroup.value.width * mmToPixels) - 30) / 90;
                options.height = (this.formGroup.value.height * mmToPixels) - 66;
            } else if (this.formGroup.value.count == 30) {
                options.width = 1.3;
            }
        } else if (this.formGroup.value.count == 1) {
            options.width = ((this.formGroup.value.width * mmToPixels) - 30) / 145;
            options.height = (this.formGroup.value.height * mmToPixels) - 66;
        } else {
            options.width = 0.75;
        }
        const valueToEncode = text;
        try {
            JsBarcode('#label', valueToEncode, options);
            return document.getElementById('label').outerHTML;
        } catch (e) {
            return '';
        }
    }

    print() {
        const controls = this.formGroup.controls;
        if (this.formGroup.value.count == 1 && this.formGroup.invalid) {
            Object.keys(controls).forEach(controlName => controls[controlName].markAsTouched());
            return;
        }

        // Validate GTIN-14 before printing (block on invalid), consistent with existing validation behavior.
        if (this.labelType === 'gtin') {
            const invalid = this.labels
                .map(l => ({ sku: l?.sku, raw: l?.label, digits: this.normalizeDigits(l?.label) }))
                .find(x => x.digits.length !== 14 || !this.isValidGtin14(x.digits));
            if (invalid) {
                const skuPart = invalid.sku ? ` (SKU: ${invalid.sku})` : '';
                this.toastr.error(`Invalid GTIN-14${skuPart}. Please fix the GTIN and try again.`);
                return;
            }
        }

        localStorage.setItem('printSettings', JSON.stringify({
            count: this.formGroup.value.count, width: this.formGroup.value.width,
            height: this.formGroup.value.height
        }));
        let content = [];
        this.labels.forEach((label, ix) => {
            label.label = this.barcode(label.label);
            label.description = label.description
                .substr(0, this.formGroup.value.count > 1 ? (this.formGroup.value.count == 30 ? 40 : 27) : this.formGroup.value.width);
            let columns = {columns: []};
            if (this.formGroup.value.count == 30) {
                columns['columnGap'] = 30;
            } else {
                columns['columnGap'] = 5;
            }
            if (ix > 0) {
                columns['pageBreak'] = 'before';
            }
            for (let i = 1; i <= label.quantity; i++) {
                columns.columns.push([{
                    svg: label.label, margin: [0, this.topMargin(this.formGroup.value.count),
                        0, this.formGroup.value.count >= 40 ? -5 : -2]
                },
                    {text: label.description, fontSize: 8},
                    {
                        text: this.labelType == 'fnsku' ? ('NEW' + (label.expiryDate ? `   Best By: ${label.expiryDate}` : '')) : ' ',
                        fontSize: 8
                    }]);
                if (i % this.labelsPerRow(this.formGroup.value.count) == 0) {
                    content.push(columns);
                    columns = {columns: []};
                    if (this.formGroup.value.count == 30) {
                        columns['columnGap'] = 30;
                    } else {
                        columns['columnGap'] = 5;
                    }
                }
            }
            if (columns.columns.length) {
                while (columns.columns.length < this.labelsPerRow(this.formGroup.value.count)) {
                    columns.columns.push({});
                }
                content.push(columns);
            }
        });
        let pdfDoc = {content: content};
        if (this.formGroup.value.count == 1) {
            pdfDoc['pageSize'] = {
                width: this.formGroup.value.width * mmToPixels,
                height: this.formGroup.value.height * mmToPixels
            };
            pdfDoc['pageMargins'] = [5, 5, 5, 5];
        } else {
            pdfDoc['pageSize'] = 'LETTER';
            pdfDoc['pageMargins'] = this.formGroup.value.count == 30 ? [30, 35, 30, 35] : [20, 15, 20, 15];
        }
        pdfMake.createPdf(pdfDoc).print();
        this.activeModal.close();
    }

    topMargin(labels: number) {
        switch (labels) {
            case 30:
            case 44:
                return -2;
            default:
                return 3;
        }
    }

    labelsPerRow(labels: number) {
        switch (labels) {
            case 30:
                return 3;
            case 40:
            case 44:
                return 4;
            default:
                return 1;
        }
    }

    getFnskus(e: any) {
        let t = 1;
        let productIds = this.products.map(p => p.productId);

        this.productsService.getFnskusForStore(e.id, productIds).subscribe(res => {
            this.labels = res.map(p => {
                return {
                    sku: p.sku,
                    storeName: p.name,
                    storeType: p.type,
                    description: p.description,
                    label: p[this.labelType],
                    quantity: this.products.find(c => c.productId == p.productId).quantity || 1
                };
            });
        });
    }
}
